<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel modal__panel--search-potential">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>

      <div class="modal__main-icon modal__main-icon--potential"></div>
      <div v-if="!showSearch" class="modal__back" @click="showSearch = true"></div>
      <div class="modal__close" @click="store.commit('setShowSearchPotentialCustomerModal', false)"></div>

      <div class="form">
        <div class="navigators">
          <div class="navigator navigator--active">
            <div class="navigator__bullet"></div>
            <div class="navigator__title">{{ $t(`searchPotentialCustomers.${"title"}`) }}</div>
          </div>
        </div>
        <div class="add__form">
          <transition name="fade" mode="out-in">
            <Loading v-if="formLoading" />
            <div v-else class="add__content">
              <transition name="fade" mode="out-in">
                <div v-if="showSearch" class="" key="0">
                  <div class="form__label">{{ $t(`searchPotentialCustomers.${"phoneLabel"}`) }}</div>
                  <input type="text" class="input input--default" :class="{ 'input--error': phoneKeywordsHasError }"
                    v-model="phoneKeywords" @input="phoneKeywordsHasError = false" />
                  <button class="modal__button modal__button--submit button button--default"
                    :disabled="!canSearchPotentialCustomer" @click="searchPotentialCustomer()">
                    {{ $t(`searchPotentialCustomers.${"searchBtn"}`) }}
                  </button>
                </div>

                <div v-else class="results" key="1">
                  <div v-if="potentialCustomers">
                    <div v-for="potentialCustomer in potentialCustomers" :key="potentialCustomer.id"
                      class="contact contact--exists">
                      <div v-if="potentialCustomer.customer" @click="selectCustomer(potentialCustomer)"
                        class="contact__name">
                        <span v-if="potentialCustomer.customer.company_name">
                          {{ potentialCustomer.customer.company_name }}
                        </span>
                        <span v-if="potentialCustomer.customer.full_name">
                          {{ potentialCustomer.customer.full_name }}
                        </span>
                        <span v-if="potentialCustomer.customer.first_name || potentialCustomer.customer.last_name">
                          {{ potentialCustomer.customer.first_name }} {{ potentialCustomer.customer.last_name }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="contact">
                    {{ $t(`searchPotentialCustomers.${"noResult"}`) }}
                  </div>
                </div>
              </transition>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import api from "@/api";
import Loading from "@/partials/Loading";

defineOptions({
  name: "SearchPotentialCustomerModal"
});

const store = useStore();

// Reactive State
const showSearch = ref(true);
const formLoading = ref(false);
const phoneKeywords = ref("");
const phoneKeywordsHasError = ref(false);
const potentialCustomers = ref(null);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const canSearchPotentialCustomer = computed(() => {
  if (isSuperAdmin.value) return true;

  const authorizedIds = store.getters.authorizedTeamIds("customer_create");
  return authorizedIds === null || (Array.isArray(authorizedIds) && authorizedIds.length > 0);
});

// Methods
const searchPotentialCustomer = async () => {
  if (!canSearchPotentialCustomer.value) {
    store.commit("setShowSearchPotentialCustomerModal", false);
    return;
  }

  if (phoneKeywords.value) {
    try {
      formLoading.value = true;
      const { data } = await api({ requiresAuth: true }).get(
        `/v1/search/potential-customers?phone=${phoneKeywords.value}`
      );

      potentialCustomers.value = data.data || null;
      showSearch.value = false;
    } catch (err) {
      console.error(err);
    } finally {
      formLoading.value = false;
    }
  } else {
    phoneKeywordsHasError.value = true;
  }
};

const selectCustomer = (customer) => {
  if (!canSearchPotentialCustomer.value) {
    store.commit("setShowSearchPotentialCustomerModal", false);
    return;
  }

  store.commit("setPotentialCustomer", customer);
  store.commit("setShowSearchPotentialCustomerModal", false);
};
</script>

<style scoped lang="scss">
.add__content {
  padding-top: 30px !important;

  .form {
    &__label {
      color: var(--text-2);
      text-align: center;
      margin-bottom: 20px;
      font-size: var(--x-small);
      font-family: var(--muli-text-semi-bold);
    }


  }
}

.button {
  left: 50%;
  transform: translateX(-50%);
  bottom: 150px;
}

.modal__button {
  position: absolute;
  bottom: 40px;
  height: 40px;
  transition: all 0.3s ease-in-out;
}

.modal__button--submit {
  width: 135px;
  box-shadow: 0px 7px 18px var(--shadow-2);
  right: 100px;
}

.modal__button--submit:hover {
  background-color: var(--hover-1);
  box-shadow: 0px -7px 18px var(--shadow-2);
}

.modal__button--cancel {
  background-color: var(--bg-12);
  box-shadow: 0px 7px 18px var(--shadow-8);
  right: 260px;
}

.modal__button--cancel:hover {
  background-color: var(--bg-12);
  box-shadow: 0px -7px 18px var(--shadow-8);
}

.contact {
  box-shadow: 0px 3px 6px var(--shadow-3);
  padding: 25px ;
  text-align: center;
  border-radius: var(--radius-1);
  position: relative;
  margin-bottom: 20px;
  transition: all 0.3s ease;

  &--exists {
    cursor: pointer;

    &:hover {
      background: var(--bg-6);
      box-shadow: none;
    }
  }

  &__thumbnail {
    position: absolute;
    height: 46px;
    width: 46px;
    background: var(--bg-21);
    border-radius: 50%;
    top: 10px;
    left: 20px;
  }

  &__name {
    color: var(--text-9); 
  }

  &__address {
    margin-bottom: 6px;
  }

  &__address,
  &__city {
    color: var(--text-2);
    font-size: var(--x-small);
  }

  &__empty {
    height: 26px;
    width: 116px;
    background: var(--bg-21);
    border-radius: var(--radius-1);

    &--large {
      width: 143px;
    }
  }
}
</style>
